// src/components/DatePicker/DatePicker.tsx
import { calendarDay } from "@simplybusiness/icons";
import {
  Button,
  Icon,
  TextField,
  VisuallyHidden,
  useValidationClasses
} from "@simplybusiness/mobius";
import classNames from "classnames/dedupe";
import {
  Suspense,
  lazy,
  useEffect,
  useRef,
  useState
} from "react";

// src/utils/isTouchDevice.ts
var isTouchDevice = () => {
  if (typeof window !== "undefined") {
    return window.matchMedia("(hover: none), (pointer: coarse)").matches;
  }
  return void 0;
};

// src/components/DatePicker/utils/formatErrorMessageText.ts
import { isAfter, isBefore } from "date-fns";
var DEFAULT_INVALID_DATE_MESSAGE = "Please enter a valid date";
var DEFAULT_BEFORE_MIN = "The date you selected is before the earliest allowed date";
var DEFAULT_AFTER_MAX = "The date you selected is after the latest allowed date";
var formatErrorMessageText = (actual, min, max) => {
  const actualDate = new Date(actual);
  const minDate = min && new Date(min);
  const maxDate = max && new Date(max);
  if (minDate && isBefore(actualDate, minDate)) {
    return DEFAULT_BEFORE_MIN;
  }
  if (maxDate && isAfter(actualDate, maxDate)) {
    return DEFAULT_AFTER_MAX;
  }
  return DEFAULT_INVALID_DATE_MESSAGE;
};

// src/components/DatePicker/utils/validateDateFormat.ts
import { isMatch } from "date-fns";
var DATE_FORMAT = "yyyy-MM-dd";
var validateDateFormat = (date) => {
  if (date === "") {
    return "";
  }
  if (isMatch(date, DATE_FORMAT)) {
    return date;
  }
  throw Error(
    `DatePicker defaultValue '${date}' is invalid. The expected format is '${DATE_FORMAT}' `
  );
};

// src/components/DatePicker/DatePicker.tsx
import { jsx, jsxs } from "react/jsx-runtime";
var DatePickerModal = lazy(() => import("./DatePickerModal-CHBI5WBW.js"));
var getValidationState = (props) => {
  const { validationState, isInvalid } = props;
  if (isInvalid || validationState === "invalid") {
    return true;
  }
  if (isInvalid === false || validationState === "valid") {
    return false;
  }
  return void 0;
};
var DatePicker = (props) => {
  const {
    onChange,
    defaultValue = "",
    isDisabled,
    validationState,
    isInvalid,
    errorMessage = "",
    ...otherProps
  } = props;
  const containerRef = useRef(null);
  const [top, setTop] = useState(0);
  const inputRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [textFieldVal, setTextFieldVal] = useState(
    validateDateFormat(defaultValue)
  );
  const [isValid, setIsValid] = useState(void 0);
  const isInvalidProp = getValidationState({
    validationState,
    isInvalid: isValid === false || isInvalid
  });
  const errorMessageText = isInvalidProp ? formatErrorMessageText(textFieldVal, props.min, props.max) : errorMessage;
  const touchDevice = isTouchDevice();
  const validationClasses = useValidationClasses({
    validationState,
    isInvalid: isInvalidProp
  });
  const containerClasses = classNames(
    "mobius-date-picker__container",
    {
      "--is-disabled": isDisabled,
      "--is-touch-device": touchDevice
    },
    validationClasses
  );
  const popoverToggleClasses = classNames(
    "mobius-date-picker__field-button",
    validationClasses
  );
  const validate = () => {
    if (isOpen) return;
    const isValidInput = inputRef.current?.checkValidity();
    if (!isValidInput) {
      setIsValid(false);
    }
  };
  useEffect(() => {
    if (!inputRef.current) return;
    const validatedValue = validateDateFormat(defaultValue);
    setTextFieldVal(validatedValue);
    inputRef.current.value = validatedValue;
    setIsValid(true);
    validate();
  }, [defaultValue]);
  const togglePopoverVisibility = () => {
    setIsValid(true);
    setIsOpen(!isOpen);
  };
  const handleTextFieldChange = (event) => {
    setTextFieldVal(event.target.value);
    setIsValid(true);
  };
  const onDateSelected = (selectedDate) => {
    if (selectedDate) {
      setTextFieldVal(selectedDate);
      setIsValid(true);
      onChange?.(selectedDate);
    }
    inputRef.current?.focus();
    setIsOpen(false);
  };
  const handleBlur = (event) => {
    if (isOpen && event.relatedTarget && containerRef.current?.contains(event.relatedTarget)) {
      return;
    }
    validate();
    if (!textFieldVal) {
      setIsValid(false);
    }
    onChange?.(event.target.value);
  };
  const handleClick = (event) => {
    event.preventDefault();
    togglePopoverVisibility();
  };
  useEffect(() => {
    if (isOpen) {
      setTop(containerRef.current?.getBoundingClientRect().height || 0);
      setIsValid(true);
      return;
    }
    validate();
  }, [isOpen]);
  if (touchDevice) {
    return /* @__PURE__ */ jsx("div", { className: containerClasses, children: /* @__PURE__ */ jsx(
      TextField,
      {
        ref: inputRef,
        type: "date",
        className: "mobius-date-picker",
        onBlur: handleBlur,
        onChange: handleTextFieldChange,
        value: textFieldVal,
        isDisabled,
        isInvalid: isInvalidProp,
        ...otherProps,
        errorMessage: errorMessage || errorMessageText
      }
    ) });
  }
  return /* @__PURE__ */ jsxs("div", { className: containerClasses, ref: containerRef, children: [
    /* @__PURE__ */ jsx(
      TextField,
      {
        ref: inputRef,
        type: "date",
        className: "mobius-date-picker",
        onBlur: handleBlur,
        onChange: handleTextFieldChange,
        onClick: handleClick,
        value: textFieldVal,
        isDisabled,
        isInvalid: isInvalidProp,
        ...otherProps,
        errorMessage: errorMessage || errorMessageText,
        suffixOutside: /* @__PURE__ */ jsxs(
          Button,
          {
            className: popoverToggleClasses,
            onClick: togglePopoverVisibility,
            isDisabled,
            size: "sm",
            children: [
              /* @__PURE__ */ jsx(Icon, { size: "sm", icon: calendarDay }),
              /* @__PURE__ */ jsx(VisuallyHidden, { children: "Pick date" })
            ]
          }
        )
      }
    ),
    isOpen && /* @__PURE__ */ jsx(Suspense, { children: /* @__PURE__ */ jsx(
      DatePickerModal,
      {
        date: textFieldVal,
        isOpen,
        top,
        onSelected: onDateSelected,
        min: props.min,
        max: props.max
      }
    ) })
  ] });
};
export {
  DatePicker
};
